import React from 'react'
import { Tooltip, theme } from 'antd'
import { BadgeTag } from '@pushly/aqe/lib/components'
import dayjs from 'dayjs'
import { joinedWithAnd } from '../math/format'
import { IsoDate } from '../math/windows'
import './org-dashboard.scss'

type Props = {
    days: IsoDate[]
    /** A domain stopped sending on a weekday it normally sends on, as opposed to a day whose figures were unusually high or low. */
    operational: boolean
}

const WEEKDAY_NAMES_MONDAY_FIRST = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** Each weekday once, Monday first whatever order the days fall in, so the sentence reads as a week: "Mondays and Thursdays". */
const pluralWeekdaysOf = (days: IsoDate[]): string => {
    const weekdaysPresent = new Set(days.map((day) => dayjs(day).format('dddd')))
    return joinedWithAnd(
        WEEKDAY_NAMES_MONDAY_FIRST.filter((weekday) => weekdaysPresent.has(weekday)).map((weekday) => `${weekday}s`),
    )
}

const describeOutlier = (days: IsoDate[], operational: boolean): string => {
    const dayList = days.map((day) => dayjs(day).format('MMM D')).join(', ')
    const onDays = dayList === '' ? '' : ` on ${dayList}`
    if (!operational) return `Unusual compared with this domain's trailing 28 days${onDays}.`
    const onWeekdays = days.length === 0 ? '' : ` on ${pluralWeekdaysOf(days)}`
    return `This domain normally sends${onWeekdays} but did not${onDays}.`
}

/** The tooltip is the control's description and carries the days; the name only says that there are some. */
const spokenLabel = (dayCount: number): string => {
    if (dayCount === 0) return 'Outlier'
    return `Outlier, ${dayCount} ${dayCount === 1 ? 'day' : 'days'}`
}

/**
 * The tooltip and focus belong to a wrapper because the tag is aqe's, which draws the fill and label from the
 * warning colour so that the text keeps its contrast in both themes.
 */
export const OutlierTag: React.FC<Props> = ({ days, operational }) => {
    const { token } = theme.useToken()
    return (
        <Tooltip title={describeOutlier(days, operational)} trigger={['hover', 'focus']}>
            <span
                className="org-dashboard-outlier-tag"
                tabIndex={0}
                role="button"
                aria-label={spokenLabel(days.length)}
            >
                <BadgeTag
                    variant="filled"
                    color={token.colorWarning}
                    className="org-dashboard-tag"
                    icon={<span className="org-dashboard-outlier-diamond" />}
                    label="Outlier"
                />
            </span>
        </Tooltip>
    )
}
